import { useCallback, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { verifySignInToken } from '@wundo/client/auth.ts';
import { m } from '@/paraglide/messages.js';
import { SignInLanding } from '@/components/SignInLanding.tsx';
import { SigningIn } from '@/components/SigningIn.tsx';
import { leaveToLoginError } from '@/lib/leaveToLoginError.ts';
import { requestedRedirect } from '@/lib/requestedSignInRedirect.ts';

/*
 * Where every sign-in link lands. The link names this app rather than the API
 * so that what arrives in an inbox or a chat is an address people recognise,
 * and so that the token is spent by a press rather than by a navigation — the
 * one thing a mail sandbox can replay.
 *
 * The link carries the token and nothing else: where the person ends up, and
 * where they end up if the token is dead, were both settled when it was minted
 * and travel inside it. That is how a link requested for the staff console
 * still arrives here and then continues there.
 */
export const Route = createFileRoute('/sign-in/verify')({
  component: VerifyRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
    /*
     * Set only on a link the person asked for at the login screen. The router
     * reads `1` as a number, and validates its own normalised output again.
     */
    requested: search.requested === true || String(search.requested) === '1',
    for: search.for === 'onboarding' ? ('onboarding' as const) : ('practice' as const),
  }),
});

/* Reading the search is the route's job, so the screen itself stays renderable. */
function VerifyRoute() {
  const { token, requested, for: flavour } = Route.useSearch();

  return <VerifyPage token={token} requested={requested} flavour={flavour} />;
}

/*
 * A dead link costs its reader a new one, not the page they asked it for. Only
 * a link asked for at the login screen carries that page; any other link would
 * pick up a page from an older, unrelated request.
 */
function leaveToLoginKeepingRedirect(code: string | null): void {
  leaveToLoginError(code, requestedRedirect());
}

/** Which errand the link was for; it decides the copy and nothing else. */
export type SignInFlavour = 'practice' | 'onboarding';

export interface VerifyPageProps {
  token: string;
  /** The person asked for this link here, so there is nothing to confirm. */
  requested?: boolean;
  flavour?: SignInFlavour;
}

/**
 * One Start button in front of the door.
 *
 * A link that signs its reader in on arrival is signed in for whoever fetches
 * it first, and in a corporate mailbox that is a scanner or Microsoft's
 * sandbox replaying the address a browser just visited. Pressing Start posts
 * the token instead, which leaves nothing to replay.
 *
 * A link requested at the login screen skips the button: that browser proved
 * it asked, the token is bound to it, and the person is waiting.
 */
export function VerifyPage({
  token,
  requested = false,
  flavour = 'practice',
}: VerifyPageProps) {
  const navigate = useNavigate();
  const onboarding = flavour === 'onboarding';

  useEffect(() => {
    /* No token means the link was mangled; there is nothing to verify. */
    if (!token) {
      void navigate({
        to: '/login',
        search: { redirect: undefined, error: undefined },
        replace: true,
      });
    }
  }, [token, navigate]);

  const verify = useCallback(() => verifySignInToken(token), [token]);

  /* Nothing to press, and nowhere to press it to: the browser is already leaving. */
  if (!token) {
    return <SigningIn />;
  }

  return (
    <SignInLanding
      title={onboarding ? m.verify_onboarding_title() : m.verify_practice_title()}
      body={onboarding ? m.verify_onboarding_body() : m.verify_practice_body()}
      button={onboarding ? m.verify_onboarding_start() : m.verify_practice_start()}
      action={verify}
      onRefused={requested ? leaveToLoginKeepingRedirect : leaveToLoginError}
      autoStart={requested}
    />
  );
}
